/**
 * Lets a failed Mermaid diagram ask Coco to rewrite it.
 *
 * `remark-sanitize-mermaid` repairs the *mechanical* corruptions (smart quotes,
 * Unicode arrows, single-dash edges) and says of everything else: "they cannot
 * be repaired safely by string surgery and are intentionally out of scope
 * here." Out of scope for a regex, in scope for the model — a mismatched
 * bracket or an unquoted label is exactly the kind of thing Coco can fix when
 * shown the parse error.
 *
 * Until now the only offer on a broken diagram was Streamdown's `retry`, which
 * is `() => setCounter(c => c + 1)`: the *same* source re-rendered through the
 * *same* parser. For a structurally malformed diagram it cannot succeed, so the
 * button reliably reproduced the error and taught the user that Coco is broken.
 *
 * Like {@link CitationProvider}, this travels by context rather than props:
 * Streamdown's top-level memo compares only `children`/`shikiTheme`/
 * `isAnimating`/`mode`, so a handler passed down through `mermaid.errorComponent`
 * would go stale on a settled message. Context updates cross `React.memo`
 * boundaries; props don't.
 */

import React, { createContext, useContext, useMemo } from 'react';

/** How a broken diagram hands its source and parse error back to the chat. */
export type MermaidRepairHandler = (prompt: string) => void;

type MermaidRepairContextValue = {
  /**
   * Sends a repair request, or `undefined` when repair isn't available — no
   * provider is mounted, or the conversation is mid-turn. The error card reads
   * this to decide whether to offer the action at all, so an unavailable
   * repair is a hidden button rather than a dead one.
   */
  requestRepair: ((chart: string, error: string) => void) | undefined;
};

const MermaidRepairContext = createContext<MermaidRepairContextValue | null>(
  null
);

/**
 * The follow-up turn a failed diagram sends on the user's behalf.
 *
 * Both the source and the raw parser message go in: the error names the line
 * and the token Mermaid choked on, which is most of the diagnosis, and without
 * the source Coco would be rewriting a diagram it has to scroll back to find.
 * Phrased as an instruction to re-emit the whole corrected block, because a
 * reply that only describes the fix leaves the broken diagram on screen.
 *
 * Exported for {@link mermaid-repair-context.spec.ts}; the prompt is the part
 * of this feature most likely to need tuning against real failures.
 */
export function buildMermaidRepairPrompt(chart: string, error: string): string {
  return [
    "The Mermaid diagram you just wrote didn't parse. Please fix the syntax and",
    'reply with the corrected diagram as a complete ```mermaid block, with no',
    'other commentary.',
    '',
    'Parser error:',
    '```',
    error.trim(),
    '```',
    '',
    'The diagram that failed:',
    '```',
    chart.trim(),
    '```',
  ].join('\n');
}

/**
 * Publishes a repair action to the markdown renderer.
 *
 * `onRepair` should be omitted (not a no-op) whenever a repair can't be sent —
 * most importantly while the conversation is already streaming, since the
 * request is an ordinary user turn and would otherwise interleave with the one
 * in flight.
 */
export const MermaidRepairProvider: React.FC<{
  onRepair?: MermaidRepairHandler;
  children: React.ReactNode;
}> = ({ onRepair, children }) => {
  const value = useMemo<MermaidRepairContextValue>(
    () => ({
      requestRepair:
        onRepair === undefined
          ? undefined
          : (chart, error) => onRepair(buildMermaidRepairPrompt(chart, error)),
    }),
    [onRepair]
  );

  return (
    <MermaidRepairContext.Provider value={value}>
      {children}
    </MermaidRepairContext.Provider>
  );
};

/** Stable identity so consumers outside a provider don't churn on every render. */
const NO_REPAIR: MermaidRepairContextValue = { requestRepair: undefined };

/**
 * Repair action for {@link MermaidErrorCard}. Returns an undefined handler when
 * no provider is mounted, so `Response` degrades to the plain error card
 * outside chat.
 */
export function useMermaidRepair(): MermaidRepairContextValue {
  return useContext(MermaidRepairContext) ?? NO_REPAIR;
}
